import { EventContextProvider } from "@codat/analytics";
import {
  Button,
  CircleAlertIcon,
  DataTable,
  DateRangeInput,
  Pagination,
  SearchInput,
  SquareNewWindowIcon,
  Typography,
  useTheme,
} from "@codat/orchard-ui";
import { useUserState } from "portal-ui-remote/UserStateContext";
import React, { useState } from "react";

import ErrorBoundary from "components/ErrorBoundary/ErrorBoundary";
import Page from "components/Page";
import PageLoadingSkeleton from "components/PageLoadingSkeleton";

import { useGetPagedSyncOverviews } from "../../api/public";
import styles from "./ManagedBankFeeds.module.scss";
import { columnDefs, tableRows } from "./SyncDataTableDefinitions";
import { SyncOverviewModal } from "./SyncOverviewModal/SyncOverviewModal";

const ManagedBankFeedsSyncs = () => {
  const [pageIndex, setPageIndex] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [search, setSearch] = useState<string>("");
  const [startDateTime, setStartDateTime] = useState<string>("");
  const [endDateTime, setEndDateTime] = useState<string>("");
  const { userState } = useUserState();

  const clientId = userState?.id ?? "";
  const [{ loading, data, error }] = useGetPagedSyncOverviews(
    clientId,
    pageIndex,
    pageSize,
    startDateTime,
    endDateTime,
    search
  );

  const theme = useTheme();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSyncId, setSelectedSyncId] = useState<string>("");

  const handleRowClick = (syncId: string) => {
    setSelectedSyncId(syncId);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedSyncId("");
  };

  return (
    <Page>
      <EventContextProvider name="Sync Overviews">
        <React.Suspense fallback={<PageLoadingSkeleton />}>
          <ErrorBoundary>
            <div className={styles.headerWrapper}>
              <Typography
                variant="heading_xxl"
                as="h1"
                className={styles.header}
                data-testid="mbf-syncs-page-title"
              >
                Syncs
              </Typography>
              <Button
                variant="secondary"
                color="interface"
                href="https://docs.codat.io/"
                target="_blank"
                data-testid="mbf-syncs-documentation-button"
              >
                <div className={styles.documentationButtonWrapper}>
                  <span>See our documentation</span>
                  <SquareNewWindowIcon className={styles.icon} />
                </div>
              </Button>
            </div>

            <div className={styles.searchInputContainer}>
              <SearchInput
                className={styles.searchInput}
                id="sync-overview-search-input"
                name="sync-overview-search-input"
                showLabel
                data-testid="sync-overview-search"
                placeholder="Company ID or name"
                label="Search company"
                aria-label="Search sync overview by company ID or name"
                onSearchQueryChange={(param) => {
                  setSearch(param);
                }}
              />

              <DateRangeInput
                data-testid="date-range-picker"
                className={styles.searchInput}
                label="Execution date range"
                onRangeSelected={(dateRange) => {
                  setStartDateTime(dateRange[0]?.toISOString() ?? "");
                  setEndDateTime(() => {
                    const date = dateRange[1];
                    date?.setHours(23, 59, 59, 99);
                    return date?.toISOString() ?? "";
                  });
                }}
              />
            </div>

            {(loading || data) && !error ? (
              <>
                <DataTable
                  hasBorder
                  rowsInfo={tableRows(handleRowClick, data?.results ?? [])}
                  columnDefs={columnDefs}
                  isSkeleton={loading}
                  isLayoutAuto
                />
                {data && (
                  <Pagination
                    total={data.pages.totalResults}
                    perPage={data.pages.pageSize}
                    perPageOptions={[10, 20, 50]}
                    pageIndex={data.pages.pageNumber}
                    onUpdatePageIndex={(index) => {
                      setPageIndex(index);
                    }}
                    onUpdatePerPage={(newPerPage) => {
                      setPageSize(newPerPage);
                    }}
                    label={"pagination"}
                  />
                )}
              </>
            ) : (
              <div className={styles.noOverviewsContainer}>
                <CircleAlertIcon
                  fillColor={theme.colors["color-ui-interface-70"]}
                  className={styles.circleAlertIcon}
                />
                <Typography
                  as="p"
                  variant="heading_md"
                  color={theme.colors["color-ui-interface"]}
                >
                  No sync overviews available
                </Typography>
              </div>
            )}

            {isModalOpen && (
              <SyncOverviewModal
                isOpen={isModalOpen}
                clientId={clientId}
                syncId={selectedSyncId}
                onClose={closeModal}
              />
            )}
          </ErrorBoundary>
        </React.Suspense>
      </EventContextProvider>
    </Page>
  );
};

export default ManagedBankFeedsSyncs;
